/*
 * <size-chart> (snippets/size-chart.liquid): a "Size chart" button and a native <dialog> with the tee diagram, legend
 * and table. Behaves like the Perfect Waffle chart (sections/ad-fit-hero.liquid): sizes and in/cm are aria-pressed
 * toggles, the shown size's row is highlighted, focus moves to the close button and returns to the button that opened
 * the chart; Esc, the close button and the backdrop close it.
 *
 * Host sync:
 *   host → chart  the selected-size attribute: the chart opens on that size, its row highlighted.
 *   chart → host  "Select size X" (only rendered with a pick label) dispatches size-chart:pick (bubbles, detail.size),
 *                 then the chart closes. The host selects that size.
 *
 * Loaded as a module, so it runs once per page however often the snippet is rendered.
 */
if (!customElements.get('size-chart')) {
  customElements.define('size-chart', class extends HTMLElement {
    static get observedAttributes() {
      return ['selected-size'];
    }

    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => this.init(), { once: true });
      else this.init();
    }

    attributeChangedCallback() {
      if (!this.dlg) return;
      this.useSelected();
      if (this.dlg.open) this.render();
    }

    init() {
      const dlg = this.querySelector('[data-chart]');
      const open = this.querySelector('[data-chart-open]');
      let cfg = {};
      try { cfg = JSON.parse(this.querySelector('[data-chart-config]').textContent); } catch (e) { cfg = {}; }
      const rows = Array.isArray(cfg.rows) ? cfg.rows : [];
      if (!dlg || !open || !rows.length) return;
      this.cfg = cfg;
      this.rows = rows;
      this.openBtn = open;
      this.unit = cfg.unit === 'cm' ? 'cm' : 'in';
      this.size = (rows.find((r) => String(r.size).toLowerCase() === 'l') || rows[0]).size;
      this.dlg = dlg;
      this.useSelected();

      open.addEventListener('click', () => this.show());
      dlg.addEventListener('close', () => this.restoreFocus());
      dlg.addEventListener('click', (event) => { if (event.target === dlg) this.shut(); });
      dlg.addEventListener('keydown', (event) => { if (event.key === 'Escape' && typeof dlg.showModal !== 'function') this.shut(); });
      dlg.querySelectorAll('[data-chart-close]').forEach((b) => b.addEventListener('click', () => this.shut()));
      dlg.querySelectorAll('[data-chart-size]').forEach((b) => b.addEventListener('click', () => { this.size = b.dataset.chartSize; this.render(); }));
      dlg.querySelectorAll('[data-chart-unit]').forEach((b) => b.addEventListener('click', () => { this.unit = b.dataset.chartUnit; this.render(); }));
      const pick = dlg.querySelector('[data-chart-pick]');
      if (pick) pick.addEventListener('click', () => {
        this.dispatchEvent(new CustomEvent('size-chart:pick', { bubbles: true, detail: { size: this.size } }));
        this.shut();
      });
    }

    /* The host's size (selected-size attribute) wins when the chart has it; otherwise the last size shown stays. */
    useSelected() {
      const size = this.getAttribute('selected-size');
      if (size && this.rows.some((r) => r.size === size)) this.size = size;
    }

    show() {
      const active = document.activeElement;
      this.opener = active instanceof HTMLElement && active !== document.body ? active : this.openBtn;
      this.useSelected();
      this.render();
      if (typeof this.dlg.showModal === 'function') this.dlg.showModal();
      else this.dlg.setAttribute('open', '');
      const close = this.dlg.querySelector('[data-chart-close]');
      if (close) close.focus();
    }

    shut() {
      if (typeof this.dlg.close === 'function') this.dlg.close();
      else {
        this.dlg.removeAttribute('open');
        this.restoreFocus();
      }
    }

    restoreFocus() {
      const el = this.opener;
      this.opener = null;
      if (el && el.isConnected && typeof el.focus === 'function') el.focus({ preventScroll: true });
    }

    fmt(cm) {
      const n = Number(cm) || 0;
      return this.unit === 'cm' ? `${n} cm` : `${(n / 2.54).toFixed(1)}"`;
    }

    render() {
      const dlg = this.dlg;
      const row = this.rows.find((r) => r.size === this.size) || this.rows[0];
      this.size = row.size;
      dlg.dataset.unit = this.unit;
      ['length', 'shoulder', 'chest', 'sleeve'].forEach((k) => {
        const t = dlg.querySelector(`[data-dim="${k}"]`);
        if (t) t.textContent = this.fmt(row[k]);
      });
      dlg.querySelectorAll('[data-chart-size]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.chartSize === row.size)));
      dlg.querySelectorAll('[data-chart-unit]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.chartUnit === this.unit)));
      dlg.querySelectorAll('[data-chart-row]').forEach((tr) => tr.classList.toggle('is-on', tr.dataset.chartRow === row.size));
      const cap = dlg.querySelector('[data-chart-caption]');
      if (cap) cap.textContent = (this.cfg.caption || '').replace('[size]', row.size).replace('[unit]', this.unit === 'cm' ? 'centimeters' : 'inches');
      const pick = dlg.querySelector('[data-chart-pick]');
      if (pick) {
        pick.textContent = (this.cfg.pickLabel || '').replace('[size]', row.size);
        pick.disabled = row.pickable === false;
      }
    }
  });
}
